import { motion } from "framer-motion";

/*
  Karmaa Overseas — brand mark
  A minimal emblem uniting a sprouting seed (two leaves + stem) rising from a
  soil/horizon arc, with a gold seed accent. Represents organic agriculture,
  growth and global export. Palette: forest #1B3B36, olive #2E7D32, gold #C9A227.
*/

export function KarmaaMark({
  className = "",
  leaf = "#2E7D32",
  stem = "#1B3B36",
  accent = "#C9A227",
  ...props
}) {
  return (
    <svg
      viewBox="0 0 64 64"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      {/* soil / horizon arc */}
      <path
        d="M12 50 Q 32 60 52 50"
        stroke={accent}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      {/* stem */}
      <path
        d="M32 50 C 32 40 32 34 32 26"
        stroke={stem}
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      {/* left leaf */}
      <path
        d="M32 40 C 22 40 15 33 15 21 C 27 21 33 29 32 40 Z"
        fill={leaf}
      />
      {/* right leaf */}
      <path
        d="M32 34 C 42 34 49 27 49 15 C 37 15 31 23 32 34 Z"
        fill={stem}
      />
      {/* leaf veins */}
      <path d="M22 30 L 30 37" stroke="#F5F2E9" strokeWidth="1" strokeLinecap="round" opacity="0.55" />
      <path d="M42 24 L 34 31" stroke="#F5F2E9" strokeWidth="1" strokeLinecap="round" opacity="0.55" />
      {/* gold seed accent */}
      <circle cx="32" cy="19" r="3.4" fill={accent} />
    </svg>
  );
}

/*
  variant:
    "primary"  — icon + full wordmark + tagline (default, for hero/light bg)
    "compact"  — icon + wordmark only
    "icon"     — mark only
    "white"    — full wordmark rendered light (for dark backgrounds)
    "dark"     — full wordmark rendered dark (for light backgrounds)
*/
export default function Logo({
  variant = "primary",
  animate = false,
  className = "",
  tagline = true,
}) {
  const isLight = variant === "white";
  const markColors = isLight
    ? { leaf: "#C9A227", stem: "#F5F2E9", accent: "#C9A227" }
    : { leaf: "#2E7D32", stem: "#1B3B36", accent: "#C9A227" };

  const wordFirst = isLight ? "text-beige" : "text-forest";
  const markSize = variant === "primary" ? "w-11 h-11" : "w-9 h-9";

  const Wrapper = animate ? motion.div : "div";
  const wrapperProps = animate
    ? {
        initial: { opacity: 0, scale: 0.85 },
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
      }
    : {};

  if (variant === "icon") {
    const MarkWrapper = animate ? motion.div : "div";
    return (
      <MarkWrapper {...wrapperProps} className={className}>
        <KarmaaMark className={markSize} {...markColors} />
      </MarkWrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps} className={`flex items-center gap-2.5 ${className}`}>
      <KarmaaMark className={`${markSize} shrink-0`} {...markColors} />
      <div className="flex flex-col leading-none">
        <span className={`font-serif tracking-tight ${variant === "primary" ? "text-2xl" : "text-xl"} ${wordFirst}`}>
          Karmaa<span className="text-gold"> Overseas</span>
        </span>
        {tagline && variant === "primary" && (
          <span className={`mt-1 text-[9px] uppercase tracking-[0.32em] ${isLight ? "text-beige/60" : "text-forest/50"}`}>
            Cultivating Heritage · Exporting Excellence
          </span>
        )}
      </div>
    </Wrapper>
  );
}
